import { MineFieldGraph } from './MineFieldGraph'

/**
 * Упрощённый constraint-propagation solver без угадывания.
 * Возвращает true, если поле можно полностью открыть дедукцией.
 */
export function isSolvable(graph: MineFieldGraph, startId?: string): boolean {
  const ids = graph.ids()
  if (ids.length === 0) return false

  const hasMine = new Map<string, boolean>()
  const adj = new Map<string, string[]>()
  for (const id of ids) {
    const slot = graph.getSlot(id)!
    hasMine.set(id, slot.hasMine)
    adj.set(id, slot.neighborIds.filter((n) => graph.getSlot(n)))
  }

  const mineCountAround = (id: string): number => {
    let c = 0
    for (const n of adj.get(id) ?? []) if (hasMine.get(n)) c += 1
    return c
  }

  // Стартовая клетка не должна быть миной
  const open = new Set<string>()
  const flagged = new Set<string>()
  let seed = startId && !hasMine.get(startId) ? startId : ids.find((id) => !hasMine.get(id))
  if (!seed) return false

  const reveal = (id: string) => {
    if (open.has(id) || flagged.has(id) || hasMine.get(id)) return
    open.add(id)
    if (mineCountAround(id) === 0) {
      for (const n of adj.get(id) ?? []) reveal(n)
    }
  }
  reveal(seed)

  const safeTotal = ids.filter((id) => !hasMine.get(id)).length
  let guard = 0
  while (open.size < safeTotal && guard < ids.length * ids.length) {
    guard += 1
    let progress = false

    for (const id of open) {
      const neighbors = adj.get(id) ?? []
      const count = mineCountAround(id)
      const unknown = neighbors.filter((n) => !open.has(n) && !flagged.has(n))
      const flaggedN = neighbors.filter((n) => flagged.has(n)).length

      if (unknown.length === 0) continue

      // Все оставшиеся неизвестные — мины
      if (count - flaggedN === unknown.length) {
        for (const n of unknown) {
          flagged.add(n)
          progress = true
        }
      }

      // Все оставшиеся неизвестные — безопасны
      if (count === flaggedN) {
        for (const n of unknown) {
          reveal(n)
          progress = true
        }
      }
    }

    if (!progress) break
  }

  return open.size >= safeTotal
}
